/**
 * Timed Release Reminder
 *
 * Swaps the qty + buy area for a "Remind Me" trigger button on timed release
 * products. Button opens a shared <notification-modal>. On submit, POSTs to
 * the worker API /reminder endpoint with email + first_name + last_name + phone
 * and fires a Klaviyo "Subscribed to Timed Release" profile-tracking event.
 *
 * On timer expiry, swaps back to purchase controls.
 *
 * Listens on document for submit + optionValueChange so it survives combined
 * listing innerHTML replacements (mirrors back-in-stock-notification.js).
 */
(function () {
  'use strict';

  // Re-queried after combined-listing switches
  var purchaseControls = null;
  var reminderControls = null;
  var trigger = null;
  var modal = null;
  var form = null;
  var submitBtn = null;
  var messageEl = null;
  var originalButtonText = '';
  var successText = '';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Delegated listeners — survive innerHTML replacements by product-info.js
    // when a combined-listing sibling is selected.
    document.addEventListener('submit', handleDelegatedSubmit, true);
    document.addEventListener('optionValueChange', handleOptionValueChange);
    document.addEventListener('timed-release:expired', handleExpired);

    setupForCurrentDom();
  }

  // ---------------------------------------------------------------------------
  // DOM wiring
  // ---------------------------------------------------------------------------

  function setupForCurrentDom() {
    if (!queryElements()) return;
    applyInitialVisibility();
  }

  function queryElements() {
    var timedReleaseControls = document.querySelector('[data-timed-release-controls]');
    if (!timedReleaseControls) return false;

    purchaseControls = timedReleaseControls.querySelector('[data-purchase-controls]');
    reminderControls = timedReleaseControls.querySelector('[data-reminder-controls]');
    if (!purchaseControls || !reminderControls) return false;

    trigger = reminderControls.querySelector('[data-notification-trigger="reminder"]');
    if (!trigger) return false;

    var modalId = trigger.getAttribute('data-open-notification-modal');
    modal = modalId ? document.getElementById(modalId) : null;
    if (!modal) return false;

    form = modal.querySelector('[data-notification-form]');
    submitBtn = form ? form.querySelector('[data-notification-submit]') : null;
    messageEl = form ? form.querySelector('.notification-modal__message') : null;
    originalButtonText = submitBtn ? submitBtn.textContent.trim() : 'Remind Me';
    successText = trigger.getAttribute('data-success-text') || 'Reminder Set';

    return Boolean(form && submitBtn);
  }

  function applyInitialVisibility() {
    // Timer state check — timer may have already expired before deferred
    // script runs. Timer sets style.display='none' on expiry.
    var timer = document.querySelector('timed-release-timer');
    if (!timer || timer.style.display === 'none') {
      showPurchaseControls();
    } else {
      showReminderControls();
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  function handleDelegatedSubmit(event) {
    var submittedForm = event.target;
    if (!submittedForm.matches || !submittedForm.matches('[data-notification-form]')) return;
    // Route by the parent <notification-modal data-notification-type> — the
    // modal lives outside [data-reminder-controls] in the DOM (hoisted so
    // display:none toggles on the wrapper don't hide it), so we can't rely
    // on ancestor traversal to decide ownership.
    var modalEl = submittedForm.closest('notification-modal');
    if (!modalEl || modalEl.getAttribute('data-notification-type') !== 'reminder') return;
    event.preventDefault();
    // Re-query in case combined-listing replaced DOM
    if (!queryElements()) return;
    handleSubmit();
  }

  function handleOptionValueChange(event) {
    var detail = event.detail;
    if (!detail || !detail.isProductSwitch) return;

    // Combined listing switch — elements will be replaced. Drop refs and
    // re-init on next frame once the new DOM has settled.
    purchaseControls = null;
    reminderControls = null;
    trigger = null;
    modal = null;
    form = null;
    submitBtn = null;
    messageEl = null;
    requestAnimationFrame(setupForCurrentDom);
  }

  function handleExpired() {
    // Re-query first — combined-listing swap could have happened since init
    if (!purchaseControls || !purchaseControls.isConnected) {
      if (!queryElements()) return;
    }
    showPurchaseControls();
  }

  // ---------------------------------------------------------------------------
  // Visibility toggles
  // ---------------------------------------------------------------------------

  function showReminderControls() {
    if (purchaseControls) purchaseControls.style.display = 'none';
    if (reminderControls) reminderControls.style.display = '';
  }

  function showPurchaseControls() {
    if (reminderControls) reminderControls.style.display = 'none';
    if (purchaseControls) purchaseControls.style.display = '';
  }

  // ---------------------------------------------------------------------------
  // Form submission
  // ---------------------------------------------------------------------------

  function handleSubmit() {
    var fields = readFields(modal);

    if (!fields.email || !isValidEmail(fields.email)) {
      showMessage('Please enter a valid email address.', 'error');
      return;
    }
    if (!fields.first_name) {
      showMessage('Please enter your first name.', 'error');
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    hideMessage();

    var productId = modal.dataset.productId;
    var productTitle = modal.dataset.productTitle;
    var productHandle = modal.dataset.productHandle;
    var productImage = modal.dataset.productImage;
    var releaseTime = modal.dataset.releaseTime;

    fetch(internalAPI + '/reminder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: fields.email,
        first_name: fields.first_name,
        last_name: fields.last_name,
        phone: fields.phone,
        product_id: productId,
        release_time: releaseTime
      })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.text().then(function (text) {
            var err = new Error('Failed to set reminder: ' + response.status);
            err.status = response.status;
            err.body = text;
            throw err;
          });
        }
        return response;
      })
      .then(function () {
        // Client-side profile-tracking event — flow trigger uses the worker's
        // server-side "Trigger Timed Release Reminder" event instead.
        var _learnq = window._learnq || [];
        _learnq.push(['track', 'Subscribed to Timed Release', {
          ProductID: productId,
          ProductName: productTitle,
          Handle: productHandle,
          ImageURL: productImage,
          ReleaseDate: releaseTime,
          Email: fields.email,
          FirstName: fields.first_name,
          LastName: fields.last_name,
          Phone: fields.phone
        }]);

        // Set Klaviyo profile identifiers too so future flows can address them
        _learnq.push(['identify', {
          $email: fields.email,
          $first_name: fields.first_name,
          $last_name: fields.last_name,
          $phone_number: fields.phone
        }]);

        showSuccess();
      })
      .catch(function (error) {
        console.error('Reminder error:', error);
        var friendly = parseWorkerError(error && error.body) ||
          'Something went wrong. Please try again.';
        showMessage(friendly, 'error');
        submitBtn.disabled = false;
        submitBtn.textContent = originalButtonText;
      });
  }

  // Worker returns { success: false, error: "..." } on validation failure.
  function parseWorkerError(body) {
    if (!body) return null;
    try {
      var parsed = typeof body === 'string' ? JSON.parse(body) : body;
      if (parsed && parsed.error) return parsed.error;
    } catch (e) {}
    return null;
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  function showSuccess() {
    submitBtn.disabled = true;
    submitBtn.classList.remove('btn--black');
    submitBtn.classList.add('notification-modal__submit--success');
    submitBtn.textContent = successText;
  }

  function showMessage(text, type) {
    if (!messageEl) return;
    messageEl.textContent = text;
    messageEl.hidden = false;
    messageEl.className = 'notification-modal__message notification-modal__message--' + type;
  }

  function hideMessage() {
    if (!messageEl) return;
    messageEl.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  function readFields(modal) {
    function val(sel) {
      var el = modal.querySelector(sel);
      return el && el.value ? el.value.trim() : '';
    }
    return {
      email: val('[data-field-email]'),
      first_name: val('[data-field-first-name]'),
      last_name: val('[data-field-last-name]'),
      phone: normalisePhone(val('[data-field-phone]'))
    };
  }

  // Klaviyo requires E.164 (+countrycode + digits). The modal shows a visible
  // "+" prefix so customers know to lead with a country code; we normalise
  // whatever they typed to match:
  //   - Strip all punctuation / whitespace
  //   - Leave a leading "+" alone
  //   - AU local (10 digits starting with 0) → rewrite as +61
  //   - Otherwise prepend "+" so the server side has a valid E.164 shape to
  //     hand to Klaviyo (Klaviyo will still reject if country code is bogus;
  //     we surface their error detail to the user).
  function normalisePhone(raw) {
    if (!raw) return '';
    var cleaned = raw.replace(/[\s\-().]/g, '');
    if (!cleaned) return '';
    if (cleaned.charAt(0) === '+') return cleaned;
    if (/^0\d{9}$/.test(cleaned)) return '+61' + cleaned.slice(1);
    return '+' + cleaned;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
})();
